import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deneignementService } from '@/services/deneignementService';
import { compagnieService } from '@/services/compagnieService';
import { parkingService } from '@/services/parkingService';
import { getErrorMessage } from '@/services/api';
import axios from 'axios';
import type { CreateDeneignementDTO, ZoneDeneignement, TypeIntervention, PrioriteDeneignement, ConflitMemeJour } from '@/types';
import { ZONE_LABELS, TYPE_LABELS, PRIORITE_LABELS } from '@/types';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ExclamationTriangleIcon, PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// SFD jalonnement Contact/Large (Oct 2026)
const SITUATION_BADGE: Record<'contact' | 'large', { label: string; className: string }> = {
  contact: { label: 'Contact', className: 'bg-blue-100 text-blue-800' },
  large: { label: 'Large', className: 'bg-purple-100 text-purple-800' },
};

/** Nombre de jalons attendus selon la situation du parking et l'etat du poste */
function nombreJalons(situation: 'contact' | 'large' | null | undefined, occupe: boolean): number | null {
  if (!situation) return null;
  if (!occupe) return 1;
  return situation === 'large' ? 7 : 5;
}
import { Link } from 'react-router-dom';
import { ConfirmModal } from '@/components/common/Modal';

/**
 * =============================================================================
 * PAGE FORMULAIRE INTERVENTION
 * =============================================================================
 *
 * Formulaire unifie pour la creation et l'edition des interventions.
 *
 * MODE CREATION:
 * - Valeurs par defaut (priorite normale, type standard, zone complete)
 * - Soumission via POST /api/deneignements
 *
 * MODE EDITION:
 * - Chargement des donnees existantes via GET /api/deneignements/{id}
 * - Pre-remplissage du formulaire avec useEffect + reset()
 * - Soumission via PUT /api/deneignements/{id}
 *
 * IMPORTANT: Le useEffect avec reset() est necessaire car useForm
 * initialise les defaultValues avant que les donnees soient chargees.
 *
 * @component
 * @version 2.2
 */
export function DeneignementFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // Chargement des donnees existantes si edition
  const { data: existingData, isLoading: isLoadingData } = useQuery({
    queryKey: ['deneignement', id],
    queryFn: () => deneignementService.get(Number(id)),
    enabled: isEdit,
  });

  // Chargement des compagnies et parkings
  const { data: compagnies = [] } = useQuery({
    queryKey: ['compagnies'],
    queryFn: () => compagnieService.list(),
  });

  const { data: parkings = [] } = useQuery({
    queryKey: ['parkings'],
    queryFn: () => parkingService.list(),
  });

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting, isDirty } } = useForm<CreateDeneignementDTO>({
    defaultValues: {
      priorite: 'normale' as PrioriteDeneignement,
      typeIntervention: 'standard' as TypeIntervention,
      zone: 'complete' as ZoneDeneignement,
    },
  });

  // SFD jalonnement Contact/Large : etat du poste, gere hors react-hook-form
  // (deux gros boutons, pas un champ natif) et fusionne a la soumission.
  // En edition, affiche en lecture seule : modifiable uniquement via sa propre
  // mutation dediee (RG-05), jamais via le PUT generique du formulaire.
  const [posteOccupe, setPosteOccupe] = useState<boolean | undefined>(undefined);
  const [posteOccupeTouched, setPosteOccupeTouched] = useState(false);
  const [editingEtat, setEditingEtat] = useState(false);
  const watchedParkingId = watch('parkingId');
  const selectedParking = parkings.find((p) => p.id === Number(watchedParkingId));
  const selectedSituation = selectedParking?.situation ?? null;

  useEffect(() => {
    if (isEdit && existingData) {
      setPosteOccupe(existingData.posteOccupe ?? undefined);
    }
  }, [isEdit, existingData]);

  const updateEtatPosteMutation = useMutation({
    mutationFn: (nouvelEtat: boolean) => deneignementService.updateEtatPoste(Number(id), nouvelEtat),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', id] });
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      setPosteOccupe(updated.posteOccupe ?? undefined);
      toast.success('Etat du poste mis a jour, jalons regeneres');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // Protection contre la perte de donnees (point 2)
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [overlapConfirm, setOverlapConfirm] = useState<{
    data: CreateDeneignementDTO;
    conflits: ConflitMemeJour[];
  } | null>(null);

  // Protection fermeture onglet / rechargement navigateur
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !hasSubmitted) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, hasSubmitted]);

  // Navigation manuelle avec verification
  const handleNavigateBack = useCallback(() => {
    if (isDirty && !hasSubmitted) {
      setShowLeaveConfirm(true);
    } else {
      navigate('/deneignements');
    }
  }, [isDirty, hasSubmitted, navigate]);

  // Prerequis manquants (point 1)
  const missingCompagnies = !isEdit && compagnies.length === 0;
  const missingParkings = !isEdit && parkings.length === 0;
  const hasMissingPrerequisites = missingCompagnies || missingParkings;

  /**
   * PRE-REMPLISSAGE DU FORMULAIRE EN MODE EDITION
   *
   * Probleme resolu: useForm initialise les defaultValues au moment
   * de la creation du hook, AVANT que les donnees API soient chargees.
   *
   * Solution: Utiliser useEffect + reset() pour mettre a jour les
   * valeurs du formulaire une fois les donnees disponibles.
   *
   * Champs pre-remplis:
   * - Informations de base (date, heure, zone, type, priorite)
   * - Donnees optionnelles (epaisseur, commentaire)
   * - Relations (compagnie, parking) via leurs IDs
   */
  useEffect(() => {
    if (isEdit && existingData) {
      reset({
        dateIntervention: existingData.dateIntervention,
        heureSouhaitee: existingData.heureSouhaitee,
        zone: existingData.zone,
        typeIntervention: existingData.typeIntervention,
        priorite: existingData.priorite,
        epaisseurNeige: existingData.epaisseurNeige,
        immatriculationAvion: existingData.immatriculationAvion,
        commentaire: existingData.commentaire,
        compagnieId: existingData.compagnie?.id,
        parkingId: existingData.parking?.id,
      });
    }
  }, [isEdit, existingData, reset]);

  // Pre-remplissage du poste depuis la Carte des postes (SFD "Carte du
  // statut hivernal des postes", #C26) : redirection ROLE_COMPAGNIE depuis
  // la fiche d'un poste sans intervention active, parking pre-rempli via
  // ?parking=<id>. Simple redirection vers ce formulaire existant, pas une
  // creation integree a la carte (perimetre v1 respecte).
  useEffect(() => {
    if (isEdit) {
      return;
    }
    const parkingIdParam = searchParams.get('parking');
    if (parkingIdParam && parkings.some((p) => p.id === Number(parkingIdParam))) {
      setValue('parkingId', Number(parkingIdParam));
    }
  }, [isEdit, searchParams, parkings, setValue]);

  // Mutation pour creer/modifier
  const mutation = useMutation({
    mutationFn: (data: CreateDeneignementDTO) => {
      if (isEdit) {
        return deneignementService.update(Number(id), data);
      }
      return deneignementService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      toast.success(isEdit ? 'Intervention modifiee' : 'Intervention creee');
      navigate('/deneignements');
    },
    onError: (error, variables) => {
      if (axios.isAxiosError(error) && error.response?.data?.requiresConfirmation) {
        setOverlapConfirm({ data: variables, conflits: error.response.data.conflits || [] });
        return;
      }
      toast.error(getErrorMessage(error));
    },
  });

  const onSubmit = (data: CreateDeneignementDTO) => {
    if (!isEdit) {
      setPosteOccupeTouched(true);
      if (posteOccupe === undefined) {
        toast.error("Indiquez si le poste est occupe par un avion ou non");
        return;
      }
      if (posteOccupe && !data.immatriculationAvion) {
        toast.error("L'immatriculation est obligatoire quand le poste est occupe");
        return;
      }
      data = { ...data, posteOccupe };
    }
    setHasSubmitted(true);
    mutation.mutate(data);
  };

  if (isEdit && isLoadingData) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement de l'intervention...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          type="button"
          onClick={handleNavigateBack}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Modifier l\'intervention' : 'Nouvelle intervention de deneigement'}
        </h2>
      </div>

      {/* Alertes prerequis manquants (point 1) */}
      {missingCompagnies && (
        <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800">
              Aucune compagnie aerienne configuree.{' '}
              <Link to="/administration/compagnies" className="font-medium underline hover:text-amber-900">
                Ajoutez-en une depuis Administration
              </Link>{' '}
              avant de creer une intervention.
            </p>
          </div>
        </div>
      )}
      {missingParkings && (
        <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800">
              Aucun parking configure.{' '}
              <Link to="/administration/parkings" className="font-medium underline hover:text-amber-900">
                Ajoutez-en un depuis Administration &gt; Parkings
              </Link>{' '}
              avant de creer une intervention.
            </p>
          </div>
        </div>
      )}

      {/* Formulaire */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Informations generales</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Date */}
              <div>
                <label htmlFor="dateIntervention" className="label">
                  Date d'intervention *
                </label>
                <input
                  id="dateIntervention"
                  type="date"
                  {...register('dateIntervention', { required: 'La date est obligatoire' })}
                  className="input"
                />
                {errors.dateIntervention && (
                  <p className="text-red-500 text-sm mt-1">{errors.dateIntervention.message}</p>
                )}
              </div>

              {/* Heure */}
              <div>
                <label htmlFor="heureSouhaitee" className="label">
                  Heure souhaitee *
                </label>
                <input
                  id="heureSouhaitee"
                  type="time"
                  {...register('heureSouhaitee', { required: 'L\'heure est obligatoire' })}
                  className="input"
                />
                {errors.heureSouhaitee && (
                  <p className="text-red-500 text-sm mt-1">{errors.heureSouhaitee.message}</p>
                )}
              </div>
            </div>

            {/* Zone */}
            <div>
              <label htmlFor="zone" className="label">
                Zone *
              </label>
              <select
                id="zone"
                {...register('zone', { required: 'La zone est obligatoire' })}
                className="input"
              >
                {Object.entries(ZONE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Type intervention */}
            <div>
              <label htmlFor="typeIntervention" className="label">
                Type d'intervention *
              </label>
              <select
                id="typeIntervention"
                {...register('typeIntervention', { required: 'Le type est obligatoire' })}
                className="input"
              >
                {Object.entries(TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Priorite */}
            <div>
              <label htmlFor="priorite" className="label">
                Priorite *
              </label>
              <select
                id="priorite"
                {...register('priorite', { required: 'La priorite est obligatoire' })}
                className="input"
              >
                {Object.entries(PRIORITE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Une priorite "Critique" declenche une alerte immediate au GID par email (R10) ;
                l'envoi par SMS sera active des que la messagerie AWS SNS sera configuree.
              </p>
            </div>

            {/* Epaisseur neige */}
            <div>
              <label htmlFor="epaisseurNeige" className="label">
                Epaisseur de neige (cm)
              </label>
              <input
                id="epaisseurNeige"
                type="number"
                min="0"
                {...register('epaisseurNeige', { valueAsNumber: true })}
                className="input"
                placeholder="Ex: 15"
              />
            </div>

            {/* Commentaire */}
            <div>
              <label htmlFor="commentaire" className="label">
                Commentaire / Instructions
              </label>
              <textarea
                id="commentaire"
                rows={3}
                {...register('commentaire')}
                className="input"
                placeholder="Instructions specifiques pour l'equipe..."
              />
            </div>
          </div>
        </div>

        {/* Selection compagnie et parking */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Parking et Compagnie</h3>
          </div>
          <div className="card-body space-y-4">
            {/* Compagnie */}
            <div>
              <label htmlFor="compagnieId" className="label">
                Compagnie aerienne *
              </label>
              <select
                id="compagnieId"
                {...register('compagnieId', { required: 'La compagnie est obligatoire', valueAsNumber: true })}
                className="input"
              >
                <option value="">-- Selectionner une compagnie --</option>
                {compagnies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codeIata} - {c.nom}
                  </option>
                ))}
              </select>
              {errors.compagnieId && (
                <p className="text-red-500 text-sm mt-1">{errors.compagnieId.message}</p>
              )}
            </div>

            {/* Parking */}
            <div>
              <label htmlFor="parkingId" className="label">
                Parking *
              </label>
              <select
                id="parkingId"
                {...register('parkingId', { required: 'Le parking est obligatoire', valueAsNumber: true })}
                className="input"
              >
                <option value="">-- Selectionner un parking --</option>
                {parkings.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} - {p.nom || p.aeroport}
                  </option>
                ))}
              </select>
              {errors.parkingId && (
                <p className="text-red-500 text-sm mt-1">{errors.parkingId.message}</p>
              )}
              {selectedParking && (
                selectedSituation ? (
                  <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${SITUATION_BADGE[selectedSituation].className}`}>
                    Situation : {SITUATION_BADGE[selectedSituation].label}
                  </span>
                ) : (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    Ce parking n'a pas de situation definie : la creation sera refusee
                    (Administration &gt; Parkings).
                  </p>
                )
              )}
            </div>

            {/* SFD jalonnement Contact/Large : etat du poste */}
            <div>
              <label className="label">Etat du poste *</label>

              {isEdit && !editingEtat ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center text-sm">
                    {posteOccupe ? (
                      <PaperAirplaneIcon className="w-5 h-5 mr-2 text-blue-500" />
                    ) : (
                      <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" />
                    )}
                    <span>
                      {posteOccupe === undefined
                        ? 'Non defini (intervention anterieure au jalonnement)'
                        : posteOccupe ? 'Occupe par un avion' : 'Non occupe'}
                      {existingData?.nombreJalonsAttendus != null && (
                        <span className="text-gray-500"> - {existingData.nombreJalonsAttendus} jalon(s)</span>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingEtat(true)}
                    disabled={!existingData?.etatPosteModifiable}
                    className="text-sm text-primary-600 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                    title={!existingData?.etatPosteModifiable ? 'Verrouille : au moins un jalon a deja demarre' : ''}
                  >
                    Modifier
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPosteOccupe(true)}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        posteOccupe === true ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">Occupe par un avion</div>
                      <div className="text-xs text-gray-500">
                        {nombreJalons(selectedSituation, true) ?? '5 ou 7'} jalon(s)
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPosteOccupe(false)}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        posteOccupe === false ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium">Non occupe</div>
                      <div className="text-xs text-gray-500">Poste complet - 1 jalon</div>
                    </button>
                  </div>
                  {posteOccupeTouched && posteOccupe === undefined && !isEdit && (
                    <p className="text-red-500 text-sm mt-1">Selectionnez un etat</p>
                  )}
                  {isEdit && editingEtat && (
                    <div className="flex justify-end space-x-3 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingEtat(false);
                          setPosteOccupe(existingData?.posteOccupe ?? undefined);
                        }}
                        className="btn-secondary text-sm"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        disabled={posteOccupe === undefined || updateEtatPosteMutation.isPending}
                        onClick={() => {
                          if (posteOccupe !== undefined) {
                            updateEtatPosteMutation.mutate(posteOccupe, {
                              onSuccess: () => setEditingEtat(false),
                            });
                          }
                        }}
                        className="btn-primary text-sm"
                      >
                        {updateEtatPosteMutation.isPending ? 'Enregistrement...' : 'Confirmer'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Immatriculation avion : obligatoire si occupe, masquee sinon (RG-03) */}
            {posteOccupe !== false && (
              <div>
                <label htmlFor="immatriculationAvion" className="label">
                  Immatriculation avion {posteOccupe ? '*' : '(si present)'}
                </label>
                <input
                  id="immatriculationAvion"
                  type="text"
                  {...register('immatriculationAvion', {
                    setValueAs: (v) => (v ? String(v).toUpperCase().trim() : null),
                    pattern: {
                      value: /^[A-Z0-9-]*$/,
                      message: 'Lettres, chiffres et tirets uniquement',
                    },
                  })}
                  className="input uppercase"
                  maxLength={12}
                  placeholder="Ex: F-GKXA - laisser vide si aucun avion present"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {posteOccupe
                    ? 'Obligatoire : le poste est indique comme occupe par un avion.'
                    : 'Non obligatoire : certaines interventions de deneigement se font sans avion present sur le parking.'}
                </p>
                {errors.immatriculationAvion && (
                  <p className="text-red-500 text-sm mt-1">{errors.immatriculationAvion.message}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button type="button" onClick={handleNavigateBack} className="btn-secondary">
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending || hasMissingPrerequisites}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || mutation.isPending ? 'Enregistrement...' : (isEdit ? 'Modifier' : 'Creer')}
          </button>
        </div>
      </form>

      {/* Modale de confirmation navigation (point 2) */}
      <ConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => {
          setShowLeaveConfirm(false);
          navigate('/deneignements');
        }}
        title="Modifications non sauvegardees"
        message="Vous avez des modifications non sauvegardees. Quitter quand meme ?"
        confirmLabel="Quitter"
        cancelLabel="Rester"
        variant="warning"
      />

      {/* Modale de confirmation chevauchement non bloquant (meme parking/jour, horaire different) */}
      <ConfirmModal
        isOpen={overlapConfirm !== null}
        onClose={() => setOverlapConfirm(null)}
        onConfirm={() => {
          if (overlapConfirm) {
            mutation.mutate({ ...overlapConfirm.data, confirmerCreation: true });
            setOverlapConfirm(null);
          }
        }}
        title="Intervention deja prevue ce jour-la"
        message={
          overlapConfirm
            ? `Ce parking a deja ${overlapConfirm.conflits.length > 1 ? 'des interventions actives' : 'une intervention active'} le meme jour, a un autre horaire : ${overlapConfirm.conflits
                .map((c) => `${c.numeroOrdre} a ${c.heureSouhaitee}`)
                .join(', ')}. Creer quand meme cette intervention ?`
            : ''
        }
        confirmLabel="Creer quand meme"
        cancelLabel="Annuler"
        variant="warning"
        isLoading={mutation.isPending}
      />
    </div>
  );
}

export default DeneignementFormPage;
